import type {
  AddComponentBody,
  ApproveBody,
  ComponentConfig,
  ComponentsResponse,
  ConfigHistoryEntry,
  ConfigSaveBody,
  IncidentActionResult,
  IncidentUpdateBody,
  IncidentsResponse,
  ManualProposalBody,
  Me,
  OpenIncidentBody,
  Proposal,
  ProposalDetail,
  PublishResult,
  RedecideBody,
  RejectBody,
  ResolveIncidentBody,
  RetireBody,
  RetireResult,
  RetryBody,
  RevertBody,
  RunHistoryPage,
  Scope,
} from '@/domain/types';

import { ApiError, type ConsoleApi, type RunsQuery, type TokenSource } from './client';

/**
 * The console API over HTTP, path for path from TDD §11.2, under a configurable base URL.
 * With a `TokenSource` (CHG-050: the console signs in through Okta) every request carries
 * `Authorization: Bearer <access token>`. A 401 renews the token silently and retries the
 * request once (a refused request wrote nothing, so a write may be retried); a second 401, or no
 * new token, starts signing in again. Without one no header is sent: the open dev API.
 * The response shapes are the "console contract" in domain/types.ts.
 */
export class HttpConsoleApi implements ConsoleApi {
  private readonly base: string;
  private readonly fetchImpl: typeof fetch;
  private readonly tokens: TokenSource | undefined;

  constructor(
    baseUrl: string,
    fetchImpl: typeof fetch = (input, init) => fetch(input, init),
    tokens?: TokenSource,
  ) {
    this.base = baseUrl.replace(/\/+$/, '');
    this.fetchImpl = fetchImpl;
    this.tokens = tokens;
  }

  me() {
    return this.request<Me>('GET', '/me');
  }

  getComponents(scope: Scope) {
    return this.request<ComponentsResponse>('GET', `/scopes/${enc(scope)}/components`);
  }

  getRuns(scope: Scope, capabilityId: string, opts: RunsQuery = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(opts.limit ?? 50));
    if (opts.before) params.set('before', opts.before);
    return this.request<RunHistoryPage>(
      'GET',
      `/scopes/${enc(scope)}/components/${enc(capabilityId)}/runs?${params}`,
    );
  }

  getQueue(scope: Scope) {
    return this.request<Proposal[]>('GET', `/scopes/${enc(scope)}/proposals`);
  }

  getProposal(proposalId: string) {
    return this.request<ProposalDetail>('GET', `/proposals/${enc(proposalId)}`);
  }

  getIncidents(scope: Scope) {
    return this.request<IncidentsResponse>('GET', `/scopes/${enc(scope)}/incidents`);
  }

  approve(proposalId: string, body: ApproveBody) {
    return this.request<PublishResult>('POST', `/proposals/${enc(proposalId)}/approve`, body);
  }

  reject(proposalId: string, body: RejectBody) {
    return this.request<{ proposal: Proposal }>(
      'POST',
      `/proposals/${enc(proposalId)}/reject`,
      body,
    );
  }

  redecide(proposalId: string, body: RedecideBody) {
    return this.request<PublishResult>('POST', `/proposals/${enc(proposalId)}/redecide`, body);
  }

  retry(proposalId: string, body: RetryBody) {
    return this.request<PublishResult>('POST', `/proposals/${enc(proposalId)}/retry`, body);
  }

  createManualProposal(body: ManualProposalBody) {
    return this.request<PublishResult>('POST', `/proposals`, body);
  }

  openIncident(scope: Scope, body: OpenIncidentBody) {
    return this.request<IncidentActionResult>('POST', `/scopes/${enc(scope)}/incidents`, body);
  }

  postIncidentUpdate(scope: Scope, incidentId: string, body: IncidentUpdateBody) {
    return this.request<IncidentActionResult>(
      'POST',
      `/scopes/${enc(scope)}/incidents/${enc(incidentId)}/updates`,
      body,
    );
  }

  resolveIncident(scope: Scope, incidentId: string, body: ResolveIncidentBody) {
    return this.request<IncidentActionResult>(
      'POST',
      `/scopes/${enc(scope)}/incidents/${enc(incidentId)}/resolve`,
      body,
    );
  }

  getConfig(scope: Scope, capabilityId: string) {
    return this.request<ComponentConfig>('GET', `/config/${enc(scope)}/${enc(capabilityId)}`);
  }

  saveConfig(scope: Scope, capabilityId: string, body: ConfigSaveBody) {
    return this.request<{ config: ComponentConfig }>(
      'PUT',
      `/config/${enc(scope)}/${enc(capabilityId)}`,
      body,
    );
  }

  addComponent(scope: Scope, body: AddComponentBody) {
    return this.request<{ config: ComponentConfig }>('POST', `/config/${enc(scope)}`, body);
  }

  getConfigHistory(scope: Scope, capabilityId: string) {
    return this.request<ConfigHistoryEntry[]>(
      'GET',
      `/config/${enc(scope)}/${enc(capabilityId)}/history`,
    );
  }

  revertConfig(scope: Scope, capabilityId: string, body: RevertBody) {
    return this.request<{ config: ComponentConfig }>(
      'POST',
      `/config/${enc(scope)}/${enc(capabilityId)}/revert`,
      body,
    );
  }

  retireComponent(scope: Scope, capabilityId: string, body: RetireBody) {
    return this.request<RetireResult>(
      'POST',
      `/config/${enc(scope)}/${enc(capabilityId)}/retire`,
      body,
    );
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT',
    path: string,
    body?: unknown,
  ): Promise<T> {
    let response = await this.send(method, path, body, await this.tokens?.accessToken());
    if (response.status === 401 && this.tokens) {
      const renewed = await this.tokens.renewAccessToken().catch(() => null);
      if (renewed) response = await this.send(method, path, body, renewed);
      if (!renewed || response.status === 401) this.tokens.signInAgain();
    }
    const text = await response.text();
    const parsed: unknown = text ? safeJson(text) : null;
    if (!response.ok) {
      throw new ApiError(response.status, parsed);
    }
    this.tokens?.authorized();
    return parsed as T;
  }

  private send(
    method: string,
    path: string,
    body: unknown,
    token: string | null | undefined,
  ): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    return this.fetchImpl(`${this.base}${path}`, init);
  }
}

function enc(segment: string): string {
  return encodeURIComponent(segment);
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { error: 'unparseable_response', detail: text.slice(0, 200) };
  }
}
